import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Text,
  Table,
  Badge,
  Button,
  Tabs,
  Modal,
  Stack,
  Skeleton,
  Card,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEye, IconPhone, IconCalendar, IconMail, IconChecklist, IconPresentation, IconClipboard } from '@tabler/icons-react';
import { useMyActivities, useActivityDetail } from '@/hooks/usePortalApi';

const ACTIVITY_TYPE_COLORS: Record<string, string> = {
  call: 'blue',
  meeting: 'green',
  follow_up: 'yellow',
  email: 'cyan',
  demo: 'purple',
  task: 'gray',
};

const ACTIVITY_TYPE_ICONS: Record<string, any> = {
  call: IconPhone,
  meeting: IconCalendar,
  follow_up: IconClipboard,
  email: IconMail,
  demo: IconPresentation,
  task: IconChecklist,
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  completed: 'green',
  cancelled: 'red',
  pending: 'yellow',
};

const fmt = (n: number) => '$' + n.toFixed(2);

export default function MyActivities() {
  const [activeTab, setActiveTab] = useState<string>('upcoming');
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const statusFilter = activeTab === 'upcoming' ? 'scheduled' : activeTab === 'completed' ? 'completed' : undefined;
  const { data: activities, isLoading: activitiesLoading } = useMyActivities(statusFilter);
  const { data: activityDetail, isLoading: detailLoading } = useActivityDetail(selectedActivityId);

  const handleViewDetails = (activityId: number) => {
    setSelectedActivityId(activityId);
    openDetail();
  };

  const handleCloseDetail = () => {
    closeDetail();
    setSelectedActivityId(null);
  };

  const truncateNotes = (notes: string | null | undefined, maxLength: number = 50) => {
    if (!notes) return '';
    return notes.length > maxLength ? notes.substring(0, maxLength) + '...' : notes;
  };

  const renderActivitiesTable = () => {
    if (activitiesLoading) {
      return (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      );
    }

    if (!activities || activities.length === 0) {
      return <Text ta="center" py="xl" c="dimmed">No activities found</Text>;
    }

    return (
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Subject</Table.Th>
            <Table.Th>Assigned To</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Notes</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {activities.map((activity: any) => {
            const TypeIcon = ACTIVITY_TYPE_ICONS[activity.activity_type] || IconClipboard;
            return (
              <Table.Tr key={activity.id}>
                <Table.Td>{new Date(activity.scheduled_date).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <Badge
                    color={ACTIVITY_TYPE_COLORS[activity.activity_type] || 'gray'}
                    variant="light"
                    leftSection={<TypeIcon size={14} />}
                  >
                    {activity.activity_type}
                  </Badge>
                </Table.Td>
                <Table.Td>{activity.subject}</Table.Td>
                <Table.Td>{activity.assigned_to}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[activity.status]} variant="light">
                    {activity.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{truncateNotes(activity.notes)}</Text>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEye size={14} />}
                    onClick={() => handleViewDetails(activity.id)}
                  >
                    Details
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>My Activities</Title>
          <Text size="sm" c="dimmed">Scheduled interactions with your account team</Text>
        </div>
      </Group>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'upcoming')} mb="md">
        <Tabs.List>
          <Tabs.Tab value="upcoming">Upcoming</Tabs.Tab>
          <Tabs.Tab value="completed">Completed</Tabs.Tab>
          <Tabs.Tab value="all">All</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upcoming" pt="md">
          {renderActivitiesTable()}
        </Tabs.Panel>

        <Tabs.Panel value="completed" pt="md">
          {renderActivitiesTable()}
        </Tabs.Panel>

        <Tabs.Panel value="all" pt="md">
          {renderActivitiesTable()}
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={detailOpened}
        onClose={handleCloseDetail}
        title="Activity Details"
        size="lg"
      >
        {detailLoading ? (
          <Stack gap="xs">
            <Skeleton height={30} />
            <Skeleton height={30} />
            <Skeleton height={30} />
            <Skeleton height={100} />
          </Stack>
        ) : activityDetail ? (
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>Subject</Text>
                  <Text>{activityDetail.subject}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Type</Text>
                  <Badge
                    color={ACTIVITY_TYPE_COLORS[activityDetail.activity_type] || 'gray'}
                    variant="light"
                  >
                    {activityDetail.activity_type}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Scheduled Date</Text>
                  <Text>{new Date(activityDetail.scheduled_date).toLocaleDateString()}</Text>
                </Group>
                {activityDetail.scheduled_time && (
                  <Group justify="space-between">
                    <Text fw={600}>Time</Text>
                    <Text>{activityDetail.scheduled_time}</Text>
                  </Group>
                )}
                <Group justify="space-between">
                  <Text fw={600}>Assigned To</Text>
                  <Text>{activityDetail.assigned_to}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Status</Text>
                  <Badge color={STATUS_COLORS[activityDetail.status]} variant="light">
                    {activityDetail.status}
                  </Badge>
                </Group>
              </Stack>
            </Card>

            {activityDetail.description && (
              <>
                <Divider label="Description" labelPosition="center" />
                <Card withBorder>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {activityDetail.description}
                  </Text>
                </Card>
              </>
            )}

            {activityDetail.notes && (
              <>
                <Divider label="Notes" labelPosition="center" />
                <Card withBorder>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {activityDetail.notes}
                  </Text>
                </Card>
              </>
            )}

            {activityDetail.related_opportunity && (
              <>
                <Divider label="Related Opportunity" labelPosition="center" />
                <Card withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={600}>Opportunity Name</Text>
                      <Text>{activityDetail.related_opportunity.name}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={600}>Stage</Text>
                      <Badge variant="light">{activityDetail.related_opportunity.stage}</Badge>
                    </Group>
                    {activityDetail.related_opportunity.value && (
                      <Group justify="space-between">
                        <Text fw={600}>Value</Text>
                        <Text>{fmt(activityDetail.related_opportunity.value)}</Text>
                      </Group>
                    )}
                  </Stack>
                </Card>
              </>
            )}

            {activityDetail.completed_date && (
              <Card withBorder>
                <Group justify="space-between">
                  <Text fw={600}>Completed Date</Text>
                  <Text>{new Date(activityDetail.completed_date).toLocaleDateString()}</Text>
                </Group>
              </Card>
            )}
          </Stack>
        ) : (
          <Text ta="center" c="dimmed">Activity not found</Text>
        )}
      </Modal>
    </Container>
  );
}
